/**
 * User Preferences Types
 *
 * Type definitions for user preferences and favorites functionality
 */

import { ItemMetadata } from '../common/api.types';

/**
 * Favorite type enum matching backend GraphQL enum
 */
export enum FavoriteType {
  PRODUCT_LINE = 'PRODUCT_LINE',
  RPN = 'RPN',
  FPN = 'FPN',
}

/**
 * Favorite action enum for tracking what happened to a favorite
 */
export enum FavoriteAction {
  ADDED = 'ADDED',
  REMOVED = 'REMOVED',
}

/**
 * Input type for createOrUpdateUserFavorites mutation
 * Matches backend GraphQL schema: CreateOrUpdateUserFavoritesInput
 */
export interface CreateOrUpdateUserFavoritesInput {
  product_line_ids?: number[] | null;
  rpn_ids?: number[] | null;
  fpn_ids?: number[] | null;
  updated_by?: string | null;
}

/**
 * Generic mutation response from backend
 * Matches backend GraphQL schema: MutationResponse
 */
export interface MutationResponse {
  numberOfRecordsUpdated?: number | null;
}

/**
 * User Preferences State
 * Redux state structure for user preferences
 */
export interface UserPreferencesState {
  isLoadingPreferences: boolean;
  preferences: UserPreferencesDetails | null;
  preferencesError: string | null;
  hasAttemptedPreferencesFetch: boolean;

  isLoadingFavorites: boolean;
  favorites: UserFavoritesResponse | null;
  favoritesError: string | null;
  hasAttemptedFavoritesFetch: boolean;

  isTogglingFavorite: boolean;
  toggleFavoriteError: string | null;
}

/**
 * User Preferences Details
 * Represents user-specific preferences in JSON format
 */
export interface UserPreferencesDetails {
  user_alias: string;
  preferences: string; // JSON string containing user preferences
  item_metadata: ItemMetadata;
}

/**
 * Input type for updating user preferences
 * TODO: Define once update API is implemented
 */
export type UserPreferencesInput = Partial<Omit<UserPreferencesDetails, 'item_metadata'>>;

/**
 * User Favorites Response (New Simplified API)
 * Returns only IDs grouped by type
 */
export interface UserFavoritesResponse {
  product_line_ids: number[];
  rpn_ids: number[];
  fpn_ids: number[];
}

/**
 * Toggle User Favorite Input
 * Input for optimistic UI updates when toggling favorites
 */
export interface ToggleUserFavoriteInput {
  favorite_type: FavoriteType;
  item_id: number;
  /** Current favorite status - true if currently favorited (will be removed), false if not (will be added) */
  isFavorited: boolean;
}

/**
 * Update User Favorites Response
 * Response from the createOrUpdateUserFavorites mutation
 */
export interface UpdateUserFavoritesResponse {
  success: boolean;
  action: FavoriteAction;
  favorite_type: FavoriteType;
  item_id: number;
}

/**
 * @deprecated - Will be removed once new API is implemented
 * Legacy User Favorites Details from old listUserFavoritesDetails API
 */
export interface UserFavoritesDetailsLegacy {
  favorite_id: number;
  favorite_type: string;
  display_order: number | null;
  product_line_id: number | null;
  product_line_alias: string | null;
  rpn_id: number | null;
  rpn_alias: string | null;
  fpn_id: number | null;
  fpn_alias: string | null;
  user_alias: string;
  item_metadata: ItemMetadata;
}
